import { useToast } from 'vue-toastification';
import CryptoJS from 'crypto-js';

const toast = useToast();

/**
 * Decrypts a ciphertext using AES-256-CBC with the provided key
 * @param {string} ciphertext - Base64 encoded string containing IV and encrypted data
 * @param {string} key - The decryption key
 * @returns {string} Decrypted string
 */
export const decrypt = (ciphertext, key) => {
  try {
    if (!ciphertext || !key) {
      throw new Error('Missing required parameters: ciphertext and key are required');
    }

    // Decode the base64 ciphertext
    const encryptedData = CryptoJS.enc.Base64.parse(ciphertext);
    
    // Extract IV (first 16 bytes) and actual ciphertext
    const iv = CryptoJS.lib.WordArray.create(encryptedData.words.slice(0, 4));
    const encrypted = CryptoJS.lib.WordArray.create(encryptedData.words.slice(4));
    
    // Hash the key to ensure it's the correct length
    const hashedKey = CryptoJS.SHA256(key);
    
    // Decrypt the data
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encrypted },
      hashedKey,
      { 
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );
    
    // Convert to string
    const result = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!result) {
      throw new Error('Decryption failed - possibly incorrect key or corrupted data');
    }
    
    return result;
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error(`Failed to decrypt data: ${error.message}`);
  }
};

export const captureRedirectUrl = async ({ commit }) => {
  const url = window.location.href.split('?')[1];
  if (!url) return;
  const path = url.split('=')[1]?.replace(process.env.VUE_APP_BASE_URL, '');
  commit('global/setRedirectUrl', path);
};

export const alert = (message, type = 'info', timeout = 3000) => {
  switch (type) {
    case 'info':
      toast.info(message, {
        timeout,
        hideProgressBar: true,
      });
      break;
    case 'success':
      toast.success(message, {
        timeout,
        hideProgressBar: true,
      });
      break;
    case 'error':
      toast.error(message, {
        timeout,
        hideProgressBar: true,
      });
      break;

    default:
      toast.info(message, {
        timeout,
        hideProgressBar: true,
      });
      break;
  }
};

export const role = (job) => job.role_title || job.job.title_name;

export const createJobLink = (job) => {
  const company_name = job?.company?.toLowerCase().replaceAll(' ', '-');
  const title = role(job)?.toLowerCase().replaceAll(' ', '-');
  const job_link = `/${company_name}/${job.fid}/${title.replaceAll('/', '-')}`;
  return job_link;
};

export const saveResumeUrl = ({ commit }, url) => {
  const paths = url?.split('/');
  if (!paths) {
    alert('no resume to display', 'info');
    return;
  }
  const key = paths[paths.length - 1];
  commit('global/setResumeUrl', { key: key.split('?')[0], url });
  const href = `/file/${key}`;
  const a = document.createElement('a');
  a.setAttribute('href', href);
  a.setAttribute('target', '_blank');
  a.click();
};

export const sleep = async (time) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, time);
  });
};
export const checkStatus = (staus) => {
  return staus === true;
};

export const formatNumber = (amount) => {
  return parseFloat(amount)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, '$&,');
};

export const formatString = (str) => {
  if (str) {
    return str.split('_').join(' ');
  } else {
    return '';
  }
};

export const getCountry = (country) => {
  try {
    const values = Object.values(country);
    return values[2];
  } catch (error) {}
};

export const errorMessage = (error) => {
  if (error?.response) {
    return error?.response?.data?.message;
  }
  if (error?.request) {
    return error?.request?.message;
  }

  return error?.message;
};

export const badgeColor = (status) => {
  // if (!['Pending','Reviewed','Interview','Hired','Rejected'].includes(status)) throw error('...')
  return (
    {
      pending: 'bg-[#1890ff] text-white',
      hold: 'bg-[#ef6c00] text-white',
      review: 'bg-[#9e9d24] text-white',
      shortlisted: 'bg-[#689f38] text-white',
      interviewing: 'bg-[#455a64] text-white',
      hired: 'bg-[#2e7d32] text-white',
      rejected: 'bg-[#b71c1c] text-white',
      internal_review: 'bg-[#303f9f] text-white',
      assessment_stage: 'bg-[#6a1b9a] text-white',
    }[status.toLowerCase()] || 'bg-brand-primary text-white'
  );
};

export const intNum = (val) => {
  return isNaN(Number(val)) ? 0 : parseInt(val);
};

const currencies = {
  usd: '$',
  ngn: '₦',
  eur: '€',
  zar: 'R',
  egp: 'E£',
  cad: 'C$',
  kes: 'Ksh',
  gbp: '£',
  aoa: 'Kz',
  ghs: 'GH₵',
  tzs: 'TSh',
};

export const currencySymbol = (currency) => {
  return !currency ? '' : currencies[currency.toLowerCase()];
};

export const nameInitials = (name) => {
  if (!name) return 'N/A';
  const nameList = name.split(' ');
  if (nameList.length >= 2) {
    return nameList[0][0] + nameList[1][0];
  }
};

export const randomBg = () => {
  const bgList = ['bg-[#4a148c]', 'bg-[#00695c]', 'bg-[#01579b]', 'bg-[#827717]', 'bg-[#e65100]', 'bg-[#4e342e]'];
  const idx = Math.floor(Math.random() * (5 - 0) + 0);
  const colorIdx = bgList[idx];
  return colorIdx || 'bg-brand-black';
};
